/**
 * <free-shipping-bar>
 *
 * Keeps a free-shipping progress bar in sync with the cart for instances that
 * live OUTSIDE the cart section (sitewide strip, homepage, etc.). Instances
 * inside the cart page/drawer are refreshed by the theme's cart morph, but this
 * listener is harmless there and keeps everything consistent.
 *
 * Listens for the theme `cart:update` event (see assets/events.js) and re-fetches
 * the authoritative cart total from /cart.js, because some cart events (e.g. add)
 * do not carry a full cart object in their payload.
 */
class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.messageEl = this.querySelector('.fsb__message');
    this.fillEl = this.querySelector('.fsb__fill');
    this.trackEl = this.querySelector('.fsb__track');

    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.msgProgress = this.dataset.msgProgress || '';
    this.msgUnlocked = this.dataset.msgUnlocked || '';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    this._onCartUpdate = this._onCartUpdate.bind(this);
    document.addEventListener('cart:update', this._onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this._onCartUpdate);
  }

  _onCartUpdate() {
    const cartUrl =
      (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) || '/cart';
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((cart) => {
        if (cart && typeof cart.total_price === 'number') this.render(cart.total_price);
      })
      .catch(() => {
        /* network hiccup — leave the last rendered state in place */
      });
  }

  render(total) {
    const threshold = this.threshold;
    const remaining = Math.max(threshold - total, 0);
    const percent = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 100;
    const qualified = threshold > 0 && total >= threshold;

    if (this.fillEl) this.fillEl.style.width = `${percent}%`;
    if (this.trackEl) this.trackEl.setAttribute('aria-valuenow', String(percent));
    this.classList.toggle('fsb--qualified', qualified);

    if (this.messageEl) {
      if (qualified) {
        this.messageEl.textContent = this.msgUnlocked;
      } else {
        const amount = `<span class="fsb__amount">${formatMoney(remaining, this.moneyFormat)}</span>`;
        this.messageEl.innerHTML = escapeForMessage(this.msgProgress).replace('[amount]', amount);
      }
    }
  }
}

/**
 * Escapes a merchant-provided message string for safe innerHTML use, leaving the
 * [amount] token intact so it can be replaced with the formatted amount span.
 * @param {string} str
 * @returns {string}
 */
function escapeForMessage(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Minimal Shopify money formatter (supports the common money_format tokens).
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;

    if (isNaN(number) || number == null) return '0';

    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  let value = '';
  const match = (format || '').match(placeholderRegex);
  const token = match ? match[1] : 'amount';

  switch (token) {
    case 'amount':
      value = withDelimiters(cents, 2, ',', '.');
      break;
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = withDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = withDelimiters(cents, 0, ' ', '');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
  }

  return (format || '${{amount}}').replace(placeholderRegex, value);
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
